import Axios, { AxiosRequestConfig } from 'axios';
import { appConfig } from '../config/appConfig';
import { showError } from '../helpers/showError';
import { ProblemDetails } from 'helpers/problemDetails';
import { RouterChildContext, matchPath } from 'react-router-dom';
import { Benchmark, DayRange, General, Job } from './errorCodes';
import { useEffect } from 'react';

const baseURL: string = appConfig.apiBaseUrl;
const axiosClient = Axios.create({
  baseURL,
  timeout: 120000,
} as AxiosRequestConfig);

const MODAL_DISABLED_ERROR_CODES = Object.values<string>([
  Benchmark.NameInUse,
  General.RequiredStringIsMissing,
  Job.NameInUse,
  Job.DayRangeFromDayCannotEqualToDay,
  Job.DayRangeFromDayMustBeMonthEnd,
  Job.DayRangeToDayMustBeMonthEnd,
  DayRange.ToDayCannotBeBeforeFromDay,
  Benchmark.LineItemsWithDuplicateWeightingComponents,
]);

const MODAL_DISABLED_PATHS = [
  '/:dataEnvironment/benchmarks',
  '/:dataEnvironment/jobs',
  '/:dataEnvironment/jobs/:jobId',
  '/:dataEnvironment/benchmarks/:benchmarkId',
];

const interceptors: Record<string, number> = {};

export function useInterceptors(
  history: RouterChildContext['router']['history']
) {
  useEffect(() => {
    interceptors.request = axiosClient.interceptors.request.use(
      (config) => {
        config.params = config.params || {};

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    interceptors.response = axiosClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (401 === error.response?.status) {
          if (sessionStorage.getItem('jwt') !== null) {
            sessionStorage.removeItem('jwt');
            showError('Your session expired, please login again');
          }
          window.location.href = appConfig.loginUrl;
          return Promise.reject(error);
        }

        if (403 === error.response?.status) {
          // TODO: user state
          window.sessionStorage.setItem('hasAccess', 'false');
          history.push(appConfig.noAccessPage);
          return Promise.reject(error);
        }

        if (400 === error.response?.status) {
          const errorDetails = new ProblemDetails(error.response?.data);

          if (
            matchPath(errorDetails.instance, {
              path: MODAL_DISABLED_PATHS,
              strict: true,
              exact: true,
            })
          ) {
            errorDetails.errors.forEach((e) => {
              if (!MODAL_DISABLED_ERROR_CODES.includes(e.code)) {
                showError(e.message);
              }
            });
          } else {
            showError(error.response?.data?.detail);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axiosClient.interceptors.request.eject(interceptors.request);
      axiosClient.interceptors.response.eject(interceptors.response);
    };
  }, []);
}

export { axiosClient };
